import Close from '@mui/icons-material/Close'
import ContentCut from '@mui/icons-material/ContentCut'
import Send from '@mui/icons-material/Send'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { PRINT_PAGE_TYPES } from 'src/consumer/constants'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { VitalShowingComponent } from 'src/mentor/components/VitalsListingPage/VitalShowingComponent'
import {
  getImageLink,
  getPriceStringWithCurrency,
  getPriceWordsStringWithCurrency,
  isCommonResponseSuccessful,
} from 'src/mentor/helpers/utilityFunctions'
import ReciptPrintingCompatibilityModeSelector from './components/ReciptPrintingCompatibilityModeSelector'
import ReciptPrintingPageSizeSelector from './components/ReciptPrintingPageSizeSelector'
import {
  KeyItem,
  ReceiptKVPair,
  ReceiptPrintAppointmentDetails,
  ReceiptPrintBrandHeading,
  ReceiptPrintClinicBlock,
  ReceiptPrintClinicBlockWrapper,
  ReceiptPrintClinicSubtext,
  ReceiptPrintContainer,
  ReceiptPrintDialogContent,
  ReceiptPrintMainHeading,
  ReceiptPrintMainSubHeading,
  ReceiptReceiptId,
  RecieptPriceBreakDownTable,
  ValueItem,
} from './styledComponents'
import {
  getEventTypeDisplayName,
  getEventTypeToMentorType,
  getGenderString,
  getPatientAgeWhenEventWasCreated,
  getPaymentMethodTitle,
  getStringsForPrintReceipt,
} from './utils'
import { getPatientVitalForBookedEvent } from '../utils'
import logoBrand from 'src/assets/logo_brand.png'
import { getCountries, getCountryCallingCode, isValidPhoneNumber } from 'libphonenumber-js'

const ENABLE_COMPATITBILITY_MODE_FEATURE = true

const ENABLE_PAGE_SIZE_FEATURE = true

// const ENABLE_PREVIOUS_VISIT_FEATURE = true

const SEND_RECEIPT_API_SUPPORT = '/api/secure/cc/support/receipt/send'
const SEND_RECEIPT_API_CLINIC = '/api/secure/clinic-assistant/receipt/send'

// Country options for WhatsApp: countryCode + dial code, sorted by dial code then country
function getReceiptCountryOptions() {
  try {
    const countries = getCountries()
    const list = countries.map((countryCode) => ({
      countryCode,
      dialCode: getCountryCallingCode(countryCode),
      label: `+${getCountryCallingCode(countryCode)} ${countryCode}`,
    }))
    list.sort((a, b) => {
      const d = (a.dialCode || '').localeCompare(b.dialCode || '')
      return d !== 0 ? d : (a.countryCode || '').localeCompare(b.countryCode || '')
    })
    return list
  } catch {
    return [{ countryCode: 'IN', dialCode: '91', label: '+91 IN' }]
  }
}

const RECEIPT_COUNTRY_OPTIONS = getReceiptCountryOptions()

export default function ReceiptPrintingComponent(props = {}) {
  const {
    bookedEvent,
    bookedEventId,
    closeDialog,
    patientSearchApiSignature,
    currentClinic: currentClinicProp,
    fetchDataWithoutParams: fetchDataWithoutParamsProp,
    sendReceiptApiSignature,
    postDataForSendReceipt,
    documentKindLabel = 'Receipt',
    openSendDialogOnReady = false,
  } = props
  const clinicDataContext = useContext(ClinicDataContext)
  const assistantData = clinicDataContext?.assistantData
  const [patient, setPatient] = useState()
  const [loading, setLoading] = useState(true)
  const [showInHindi, setShowInHindi] = useState(true)
  const [latestVital, setLatestVital] = useState(null)
  const [showVital, setShowVital] = useState(true)
  const [previousBookingWithThisMentor, setPreviousBookingWithThisMentor] = useState()

  const [compatibilityMode, setCompatibilityMode] = useState(false)
  const [printPageSize, setPrintPageSize] = useState(PRINT_PAGE_TYPES?.a6 ?? 'a6')
  const [showLastVisit, setShowLastVisit] = useState(true)
  const [showContact, setShowContact] = useState(false)
  const [showAumhumLogo, setShowAumhumLogo] = useState(true)

  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [selectedContactKeys, setSelectedContactKeys] = useState({})
  const [customWhatsAppCountryCode, setCustomWhatsAppCountryCode] = useState('IN')
  const [customWhatsAppNumber, setCustomWhatsAppNumber] = useState('')
  const [customWhatsAppError, setCustomWhatsAppError] = useState(null)
  const [customEmail, setCustomEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [sendSuccess, setSendSuccess] = useState(false)

  const receiptContentRef = useRef(null)
  const autoOpenedSendDialogRef = useRef(false)

  const isSupportPortal = patientSearchApiSignature?.includes('cc/support') ?? false
  const sendReceiptApi = sendReceiptApiSignature ?? (isSupportPortal ? SEND_RECEIPT_API_SUPPORT : SEND_RECEIPT_API_CLINIC)
  const postSendReceipt = postDataForSendReceipt

  const langCode = showInHindi ? 'hi' : 'en'

  const strings = getStringsForPrintReceipt(langCode)

  const fetchData = fetchDataWithoutParamsProp || getCPDataWithoutParams
  const currentClinic =
    currentClinicProp ||
    assistantData?.associatedClinicsData?.find((clinic) => clinic?.id === bookedEvent?.clinicId)

  const isBrandImageAvailable =
    currentClinic?.clinicBrandIconImage || currentClinic?.clinicBrandCoverIcon

  let patientAgeWhenEventWasCreated = getPatientAgeWhenEventWasCreated(
    patient?.dateOfBirth,
    bookedEvent?.createdTime,
  )

  // const isBrandImageAvailable = false

  const appointmentCounterString = bookedEvent?.appointmentNumber?.replace(/\D/g, '') ?? ''
  const brandPrefix = (
    currentClinic?.clinicBrandShortName ||
    currentClinic?.brandName ||
    currentClinic?.title ||
    bookedEvent?.clinicBrandData?.shortName ||
    bookedEvent?.clinicBrandName ||
    bookedEvent?.clinicBrandData?.brandName ||
    bookedEvent?.clinicLocation?.title ||
    bookedEvent?.clinicName ||
    ''
  )
    .substring(0, 2)
    .toUpperCase()

  function printReceipt() {
    document.body.classList.add('printing-receipt')
    const printDelayMs = 150
    setTimeout(() => {
      const body = document.body
      let printRoot = document.getElementById('receipt-print-root')
      if (!printRoot) {
        printRoot = document.createElement('div')
        printRoot.id = 'receipt-print-root'
        body.appendChild(printRoot)
      }
      printRoot.innerHTML = ''
      const source = receiptContentRef.current
      if (source) {
        const clone = source.cloneNode(true)
        printRoot.appendChild(clone)
      }
      window.print()
      const cleanup = () => {
        document.body.classList.remove('printing-receipt')
        const root = document.getElementById('receipt-print-root')
        if (root) root.innerHTML = ''
      }
      window.addEventListener('afterprint', cleanup, { once: true })
      setTimeout(cleanup, 2000)
    }, printDelayMs)
  }

  async function getAndSetPatientDetails() {
    const resp = await fetchData(
      `${patientSearchApiSignature}?patientId=${bookedEvent?.patientId}&clinicId=${bookedEvent?.clinicId}`,
    )
    if (resp?.status === 200) {
      const foundPatient = resp?.data?.[0]
      setPatient(foundPatient)
    }
  }

  async function getAndSetPatientVitalsForBookedEvent() {
    const vitalForBookedEvent = await getPatientVitalForBookedEvent(
      bookedEvent?.patientId,
      bookedEvent?.clinicId,
      bookedEvent?.id,
    )
    if (vitalForBookedEvent) {
      setLatestVital(vitalForBookedEvent)
    }
  }

  async function setup() {
    if (!bookedEvent?.patientId) {
      setLoading(false)
      return
    }
    setLoading(true)
    await Promise.all([
      getAndSetPatientDetails(),
      getAndSetPatientVitalsForBookedEvent(),
      getPreviousBooking(),
    ])
    setLoading(false)
  }

  const clinicDisplayName =
    currentClinic?.clinicBrandName ||
    currentClinic?.brandName ||
    bookedEvent?.clinicBrandName ||
    bookedEvent?.clinicBrandData?.brandName ||
    bookedEvent?.clinicBrandData?.name ||
    bookedEvent?.clinicBrandData?.title ||
    ''
  const clinicDisplayLocationTitle =
    currentClinic?.title ||
    currentClinic?.clinicLocationName ||
    bookedEvent?.clinicLocation?.title ||
    bookedEvent?.clinicLocation?.name ||
    bookedEvent?.clinicName ||
    bookedEvent?.clinicLocationName ||
    bookedEvent?.clinicLocationTitle ||
    ''
  const titleForHeading = clinicDisplayName || clinicDisplayLocationTitle
  const subtextForSubheading = clinicDisplayName ? clinicDisplayLocationTitle : ''
  const clinicDisplayAddress =
    currentClinic?.location?.address ||
    currentClinic?.address ||
    bookedEvent?.clinicLocation?.address ||
    bookedEvent?.location?.address ||
    bookedEvent?.address ||
    bookedEvent?.clinicAddress ||
    bookedEvent?.clinicLocationAddress ||
    bookedEvent?.eventData?.clinicLocation?.address ||
    ''
  const clinicDisplayMobile =
    currentClinic?.mobile ||
    currentClinic?.location?.mobile ||
    bookedEvent?.clinicLocation?.mobile ||
    bookedEvent?.eventData?.clinicLocation?.mobile ||
    bookedEvent?.mobile ||
    ''

  function appointmentDetails() {
    return (
      <div style={{ display: 'block' }}>
        <ReceiptPrintBrandHeading>{titleForHeading || '\u00A0'}</ReceiptPrintBrandHeading>
        <ReceiptPrintClinicSubtext>{subtextForSubheading || '\u00A0'}</ReceiptPrintClinicSubtext>
        <ReceiptPrintClinicBlock>{clinicDisplayAddress || '\u00A0'}</ReceiptPrintClinicBlock>
        {clinicDisplayMobile ? (
          <ReceiptPrintClinicBlock>
            {strings.clinicPhone} {clinicDisplayMobile}
          </ReceiptPrintClinicBlock>
        ) : null}
      </div>
    )
  }

  async function getPreviousBooking() {
    if (!bookedEvent?.patientId) return
    setPreviousBookingWithThisMentor()
    const bookedSlotsPatientApi =
      typeof fetchDataWithoutParamsProp === 'function'
        ? '/api/secure/cc/support/booked-event-history-of-a-patient'
        : '/api/secure/clinic-assistant/booked-slots/patient'
    const response = await fetchData(
      `${bookedSlotsPatientApi}?patientId=${bookedEvent.patientId}&clinicId=${bookedEvent.clinicId ?? ''}&sortDescendingOnStartTime=true&limit=10`,
    )
    if (
      response?.status === 200 &&
      isCommonResponseSuccessful(response?.data?.code) &&
      response?.data?.data?.['booked-event-info']
    ) {
      let arrayOfPastBookings = response?.data?.data?.['booked-event-info']
      const arrayOfPastBookingsWithThisMentor = arrayOfPastBookings?.filter(
        (item) => item?.mentorId === bookedEvent?.mentorId && item?.id !== bookedEvent?.id,
      )
      setPreviousBookingWithThisMentor(arrayOfPastBookingsWithThisMentor?.[0])
    }
  }

  useEffect(() => {
    setup()
  }, [bookedEvent])

  // Build contact options for Send to Patient (support: all patient contacts + custom; non-support: primary only)
  const contactOptionsForSend = useMemo(() => {
    if (!patient) return []
    const list = []
    if (patient.mobile) {
      list.push({ key: 'mobile', type: 'whatsapp', value: (patient.mobile || '').replace(/\D/g, ''), label: 'Mobile', display: patient.mobile })
    }
    if (patient.email) {
      list.push({ key: 'email', type: 'email', value: patient.email, label: 'Email', display: patient.email })
    }
    if (isSupportPortal) {
      (Array.isArray(patient.altMobiles) ? patient.altMobiles : []).forEach((v, i) => {
        if (v) list.push({ key: `altMobile_${i}`, type: 'whatsapp', value: (v || '').replace(/\D/g, ''), label: `Alt Mobile ${i + 1}`, display: v })
      })
      ;(Array.isArray(patient.altEmails) ? patient.altEmails : []).forEach((v, i) => {
        if (v) list.push({ key: `altEmail_${i}`, type: 'email', value: String(v).trim(), label: `Alt Email ${i + 1}`, display: v })
      })
    }
    return list
  }, [patient, isSupportPortal])

  const handleOpenSendDialog = useCallback(() => {
    setSendDialogOpen(true)
    setSendError(null)
    setSendSuccess(false)
    setCustomWhatsAppError(null)
    const initial = {}
    contactOptionsForSend.forEach((c) => { initial[c.key] = true })
    setSelectedContactKeys(initial)
    setCustomWhatsAppCountryCode('IN')
    setCustomWhatsAppNumber('')
    setCustomEmail('')
  }, [contactOptionsForSend])

  useEffect(() => {
    if (
      openSendDialogOnReady &&
      !loading &&
      !autoOpenedSendDialogRef.current &&
      postSendReceipt &&
      sendReceiptApi &&
      bookedEventId
    ) {
      autoOpenedSendDialogRef.current = true
      handleOpenSendDialog()
    }
  }, [bookedEventId, handleOpenSendDialog, loading, openSendDialogOnReady, postSendReceipt, sendReceiptApi])

  const validateCustomWhatsApp = (countryCode, numberDigits) => {
    if (!numberDigits || numberDigits.length < 6) return null
    const fullE164 = '+' + (getCountryCallingCode(countryCode) || '') + numberDigits
    return isValidPhoneNumber(fullE164) ? null : 'Enter a valid mobile number for the selected country.'
  }

  const handleCustomWhatsAppNumberChange = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 15)
    setCustomWhatsAppNumber(digits)
    setCustomWhatsAppError(validateCustomWhatsApp(customWhatsAppCountryCode, digits) || null)
    setSendError(null)
  }

  const handleToggleContact = (key) => {
    setSelectedContactKeys((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSendReceipt = async () => {
    if (!postSendReceipt || !sendReceiptApi || !bookedEventId) return
    const channels = []
    contactOptionsForSend.forEach((c) => {
      if (selectedContactKeys[c.key] && c.value) {
        channels.push({ type: c.type, value: c.type === 'whatsapp' ? c.value : c.value })
      }
    })
    const customNumberDigits = (customWhatsAppNumber || '').replace(/\D/g, '')
    if (customNumberDigits) {
      const fullE164 = '+' + (getCountryCallingCode(customWhatsAppCountryCode) || '') + customNumberDigits
      if (!isValidPhoneNumber(fullE164)) {
        setCustomWhatsAppError('Enter a valid mobile number for the selected country.')
        setSendError('Please correct the custom WhatsApp number.')
        return
      }
      channels.push({ type: 'whatsapp', value: (getCountryCallingCode(customWhatsAppCountryCode) || '') + customNumberDigits })
    }
    if (customEmail?.trim()) {
      channels.push({ type: 'email', value: customEmail.trim() })
    }
    if (channels.length === 0) {
      setSendError('Select at least one contact or enter a custom WhatsApp number or email.')
      return
    }
    setSending(true)
    setSendError(null)
    try {
      const res = await postSendReceipt(sendReceiptApi, {
        bookedEventId,
        languageCode: langCode,
        includeVitals: showVital && !!latestVital,
        channels,
        showAumhumLogo: !!showAumhumLogo,
      })
      if (res?.status >= 200 && res?.status < 300) {
        setSendSuccess(true)
        setTimeout(() => { setSendDialogOpen(false) }, 1500)
      } else {
        setSendError(res?.data?.message || 'Failed to send receipt.')
      }
    } catch (err) {
      setSendError(err?.response?.data?.message || err?.message || 'Failed to send receipt.')
    } finally {
      setSending(false)
    }
  }

  useEffect(() => {
    const printInHindi = localStorage.getItem('printInHindi')
    setShowInHindi(printInHindi === 'false' ? false : true)
    const showPrintVital = sessionStorage.getItem('showPrintVital')
    setShowVital(showPrintVital === 'false' ? false : true)

    // if (ENABLE_PREVIOUS_VISIT_FEATURE) {
    const printLastVisit = sessionStorage.getItem('printLastVisit')
    setShowLastVisit(printLastVisit === 'false' ? false : true)
    // }
    if (ENABLE_PAGE_SIZE_FEATURE) {
      const stored = sessionStorage.getItem('printPageSize')
      setPrintPageSize(stored ?? PRINT_PAGE_TYPES?.a6 ?? 'a6')
    }

    const storedAumhumLogo = sessionStorage.getItem('showAumhumLogoOnReceipt')
    setShowAumhumLogo(storedAumhumLogo !== 'false')

    if (ENABLE_COMPATITBILITY_MODE_FEATURE) {
      const printCompatibilityMode = sessionStorage.getItem('printComptabilityMode')
      setCompatibilityMode(printCompatibilityMode === 'false' ? false : true)
    }
    const showPrintContact = sessionStorage.getItem('showPrintContact')
    setShowContact(showPrintContact === 'true')
  }, [])

  return (
    <>
      <DialogTitle className="hide-on-print-only d-flex align-items-center justify-content-between">
        <span>Print {documentKindLabel}</span>
        <div className="d-flex align-items-center justify-content-end gap-3">
          <div className="d-flex align-items-center" style={{ fontSize: '15px' }}>
            <ToggleButtonGroup
              color="primary"
              value={showInHindi}
              exclusive
              onChange={(e, val) => {
                setShowInHindi(val)
                localStorage.setItem('printInHindi', val)
              }}
            >
              <ToggleButton sx={{ fontSize: '12px', padding: '6px' }} value={true}>
                हिंदी में प्रिंट करें
              </ToggleButton>
              <ToggleButton sx={{ fontSize: '12px', padding: '6px' }} value={false}>
                Print in English
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
          <Button disabled={loading} onClick={printReceipt} variant="contained">
            Print
          </Button>
          {postSendReceipt && sendReceiptApi && (
            <Button
              disabled={loading}
              startIcon={<Send />}
              onClick={handleOpenSendDialog}
              variant="outlined"
              color="primary"
            >
              Send to Patient
            </Button>
          )}
          <IconButton onClick={closeDialog}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <ReceiptPrintDialogContent>
        {!loading ? (
          <>
            <div className="hide-on-print-only d-flex flex-column gap-2 mb-2">
              {latestVital && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showVital}
                      onChange={(e) => {
                        setShowVital(e.target.checked)
                        sessionStorage.setItem('showPrintVital', e.target.checked)
                      }}
                    />
                  }
                  label="Print Vitals"
                />
              )}

              {previousBookingWithThisMentor?.startTime && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showLastVisit}
                      onChange={(e) => {
                        setShowLastVisit(e.target.checked)
                        sessionStorage.setItem('showLastVisit', e.target.checked)
                      }}
                    />
                  }
                  label="Show last visit in receipt"
                />
              )}

              {(patient?.mobile || patient?.email) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showContact}
                      onChange={(e) => {
                        setShowContact(e.target.checked)
                        sessionStorage.setItem('showPrintContact', e.target.checked)
                      }}
                    />
                  }
                  label={strings.printContact}
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAumhumLogo}
                    onChange={(e) => {
                      setShowAumhumLogo(e.target.checked)
                      sessionStorage.setItem('showAumhumLogoOnReceipt', e.target.checked)
                    }}
                  />
                }
                label="Show AUMHUM logo on receipt"
              />

              {ENABLE_COMPATITBILITY_MODE_FEATURE && (
                <ReciptPrintingCompatibilityModeSelector
                  value={compatibilityMode}
                  onChange={(value) => {
                    setCompatibilityMode(value)
                    sessionStorage.setItem('printComptabilityMode', value)
                  }}
                />
              )}

              {ENABLE_PAGE_SIZE_FEATURE && (
                <ReciptPrintingPageSizeSelector
                  value={printPageSize}
                  onChange={(value) => {
                    setPrintPageSize(value)
                    sessionStorage.setItem('printPageSize', value)
                  }}
                />
              )}
            </div>

            <div ref={receiptContentRef} aria-hidden="true">
              <ReceiptPrintContainer
                compatibilityMode={ENABLE_COMPATITBILITY_MODE_FEATURE && compatibilityMode}
                pageSize={ENABLE_PAGE_SIZE_FEATURE ? printPageSize : (PRINT_PAGE_TYPES?.a6 ?? 'a6')}
              >
              <div className="bordered-portion">
                <ReceiptPrintClinicBlockWrapper>
                  {showAumhumLogo && (
                    <div style={{ position: 'absolute', top: 0, right: 0 }}>
                      <img
                        src={logoBrand}
                        alt="AUMHUM"
                        style={{ height: '2em', maxWidth: '100px', objectFit: 'contain' }}
                      />
                    </div>
                  )}
                  {isBrandImageAvailable ? (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                      <img
                        src={getImageLink(
                          currentClinic?.clinicBrandIconImage,
                          currentClinic?.clinicBrandCoverIcon,
                          false,
                        )}
                        style={{ height: '2.6em', width: '2.6em' }}
                        alt="brand"
                      />
                    </div>
                  ) : null}
                  {appointmentDetails()}
                </ReceiptPrintClinicBlockWrapper>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginTop: 0,
                    marginBottom: 16,
                    gap: 12,
                  }}
                >
                  <div style={{ flexShrink: 0, marginTop: 0, paddingTop: 0 }}>
                    <KVPairComponent
                      uiKey={strings.tokenNo}
                      value={bookedEvent?.appointmentNumber}
                      higlighted
                      centerContent
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 2,
                      marginTop: 0,
                      paddingTop: 0,
                      flexShrink: 0,
                    }}
                  >
                    <ReceiptPrintMainHeading className="d-flex flex-column m-0 align-items-end" style={{ lineHeight: 1.2, margin: 0, padding: 0 }}>
                      <span>{strings.bill}</span> <span>{strings.receipt}</span>
                      <ReceiptReceiptId>
                        {dayjs.utc(bookedEvent?.startTime).local().format('YYYY-MM-DD')}-{brandPrefix}
                        -{appointmentCounterString}
                      </ReceiptReceiptId>
                    </ReceiptPrintMainHeading>
                  </div>
                </div>

                <ReceiptPrintAppointmentDetails>
                  <KVPairComponent
                    uiKey={strings.appointmentDateAndTime}
                    value={dayjs.utc(bookedEvent?.startTime).local().format('DD/MM/YYYY h:mm A')}
                  />
                  <KVPairComponent
                    uiKey={`${strings.mentorName?.replace(
                      '{mentorName}',
                      getEventTypeToMentorType(bookedEvent?.eventType, langCode) || 'Mentor',
                    )}`}
                    value={bookedEvent?.mentorName}
                  />
                  <KVPairComponent uiKey={strings.patientName} value={bookedEvent?.userName} />
                  <KVPairComponent
                    uiKey={strings.modeOfPayment}
                    value={
                      (Number(bookedEvent?.feePaid) === 0 && !bookedEvent?.feePaymentMode)
                        ? strings.notApplicable
                        : getPaymentMethodTitle(bookedEvent?.feePaymentMode, langCode) ||
                          bookedEvent?.feePaymentMode ||
                          strings.notApplicable
                    }
                  />
                  <KVPairComponent
                    uiKey={strings.ageSex}
                    value={`${
                      patient?.dateOfBirth || patient?.gender
                        ? `${patientAgeWhenEventWasCreated}/${
                            getGenderString(patient?.gender, langCode) || 'NA'
                          }`
                        : 'NA'
                    }`}
                  />
                  {previousBookingWithThisMentor?.startTime && showLastVisit ? (
                    <KVPairComponent
                      uiKey={strings.lastVisited}
                      value={`${dayjs
                        .utc(previousBookingWithThisMentor?.startTime)
                        .local()
                        .format('YYYY-MM-DD')}`}
                    />
                  ) : null}
                  <KVPairComponent uiKey={strings.patientNumber} value={patient?.patientNumber} />
                  {showContact && (patient?.mobile || patient?.email) ? (
                    <>
                      {patient?.mobile ? (
                        <KVPairComponent uiKey={strings.patientMobile} value={patient.mobile} />
                      ) : (
                        <div />
                      )}
                      {patient?.email ? (
                        <KVPairComponent uiKey={strings.patientEmail} value={patient.email} />
                      ) : (
                        <div />
                      )}
                    </>
                  ) : null}
                  {/* <KVPairComponent uiKey={`Registration No`} value={'NOT IMPLEMENTED'} /> */}
                </ReceiptPrintAppointmentDetails>
                <RecieptPriceBreakDownTable>
                  <div className="breakdown-heading">{strings.description}</div>
                  <div className="breakdown-heading right-aligned">{strings.grossAmount}</div>
                  <div className="breakdown-heading right-aligned">{strings.disc}</div>
                  <div className="breakdown-heading right-aligned">{strings.netAmount}</div>
                  <div className="item">
                    {strings.eventCharge?.replace(
                      '{eventName}',
                      getEventTypeDisplayName(bookedEvent?.eventType, langCode),
                    )}
                  </div>
                  <div className="item right-aligned">
                    {getPriceStringWithCurrency(bookedEvent?.feePaid, bookedEvent?.currency)}
                  </div>
                  <div className="item right-aligned">
                    {getPriceStringWithCurrency(0, bookedEvent?.currency)}
                  </div>
                  <div className="item right-aligned">
                    {getPriceStringWithCurrency(bookedEvent?.feePaid, bookedEvent?.currency)}
                  </div>
                </RecieptPriceBreakDownTable>
                <div style={{ marginTop: 4 }}>
                  <KVPairComponent
                    uiKey={strings.netAmount}
                    value={getPriceStringWithCurrency(bookedEvent?.feePaid, bookedEvent?.currency)}
                  />
                </div>
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  <ReceiptKVPair>
                    {getPriceWordsStringWithCurrency(
                      bookedEvent?.feePaid,
                      bookedEvent?.currency,
                      langCode,
                    )}
                  </ReceiptKVPair>
                </div>
              </div>
              {latestVital && showVital && (
                <div>
                  <div
                    className="w-100 mt-3 mb-3"
                    style={{ borderTop: '1px dotted black', position: 'relative' }}
                  >
                    <ContentCut
                      style={{ position: 'absolute', fontSize: '14px', top: '-8px', left: '32px' }}
                    />
                  </div>
                  <VitalShowingComponent vitalItem={latestVital} useEmStyles />
                </div>
              )}
              </ReceiptPrintContainer>
            </div>
          </>
        ) : (
          <div className="d-flex justify-content-center">
            <CircularProgress />
          </div>
        )}
      </ReceiptPrintDialogContent>

      <Dialog open={sendDialogOpen} onClose={() => !sending && setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send {documentKindLabel.toLowerCase()} to patient</DialogTitle>
        <DialogContent>
          {sendSuccess ? (
            <Typography color="primary">{documentKindLabel} send request submitted successfully.</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select contacts to send the {documentKindLabel.toLowerCase()} (PDF) to. On support portal you can also add a custom WhatsApp number or email.
              </Typography>
              {contactOptionsForSend.length > 0 && (
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Patient contacts</Typography>
              )}
              {contactOptionsForSend.map((c) => (
                <FormControlLabel
                  key={c.key}
                  control={
                    <Checkbox
                      checked={!!selectedContactKeys[c.key]}
                      onChange={() => handleToggleContact(c.key)}
                    />
                  }
                  label={`${c.label}: ${c.display}`}
                />
              ))}
              {isSupportPortal && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Custom contact</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="receipt-custom-whatsapp-country-label">Country</InputLabel>
                      <Select
                        labelId="receipt-custom-whatsapp-country-label"
                        label="Country"
                        value={customWhatsAppCountryCode}
                        onChange={(e) => {
                          setCustomWhatsAppCountryCode(e.target.value)
                          setCustomWhatsAppError(validateCustomWhatsApp(e.target.value, customWhatsAppNumber) || null)
                          setSendError(null)
                        }}
                      >
                        {RECEIPT_COUNTRY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.countryCode} value={opt.countryCode}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="WhatsApp number"
                      placeholder="e.g. 9876543210"
                      value={customWhatsAppNumber}
                      onChange={(e) => handleCustomWhatsAppNumberChange(e.target.value)}
                      inputProps={{ inputMode: 'tel', maxLength: 15 }}
                      error={!!customWhatsAppError}
                      helperText={customWhatsAppError}
                      sx={{ flex: 1, minWidth: 160 }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    type="email"
                    placeholder="email@example.com"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                  />
                </>
              )}
              {sendError && (
                <Typography color="error" variant="body2" sx={{ mt: 2 }}>{sendError}</Typography>
              )}
            </>
          )}
        </DialogContent>
        {!sendSuccess && (
          <DialogActions>
            <Button onClick={() => setSendDialogOpen(false)} disabled={sending}>Cancel</Button>
            <Button variant="contained" onClick={handleSendReceipt} disabled={sending} startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}>
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  )
}

function KVPairComponent({ uiKey, value, higlighted, compatibilityMode = false, centerContent = false }) {
  return (
    <ReceiptKVPair compatibilityMode={compatibilityMode}>
      <div
        className="d-flex align-items-center gap-1 px-2"
        style={{
          ...(higlighted ? { background: '#ebebeb', border: '1px solid #797979' } : {}),
          flexWrap: 'nowrap',
          ...(centerContent ? { justifyContent: 'center', textAlign: 'center' } : {}),
        }}
      >
        <KeyItem>{uiKey}</KeyItem>
        <ValueItem>{value}</ValueItem>
      </div>
    </ReceiptKVPair>
  )
}
